import React, { useState } from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// About team — faithful rewrite of legacy molecules/AboutTeam (Strapi
// sections.about-team; used for both "People behind Cymmetri" and the Advisory
// Board). A multi-card carousel of portrait cards with a name/role/LinkedIn
// caption; blue prev/next arrows below-left. Active index via client useState.
// Legacy used react-slick (3.5 cards) + hover captions; this is self-contained
// with always-visible captions. No react-slick / next/image.
export const aboutTeamSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  description: z.string().optional(),
  members: z
    .array(
      z.object({
        name: z.string().min(1),
        designation: z.string().optional(),
        linkedin: z.string().optional(),
        imageUrl: z.string().optional(),
      }),
    )
    .min(1),
});

type AboutTeamProps = z.infer<typeof aboutTeamSchema>;

const Band = styled.section`
  background: #fcfaf5;
  overflow: hidden;
`;

const Head = styled.div`
  max-width: 112rem;
  margin: 0 auto;
  padding: 4rem 1.6rem 0;

  @media (min-width: 48em) {
    padding: 14rem 1.6rem 0;
  }
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  font-size: 1.6rem;
  letter-spacing: 5px;
  text-transform: uppercase;

  @media (min-width: 48em) {
    font-size: 1.8rem;
  }
`;

const HeadRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  margin-top: 1rem;

  @media (min-width: 48em) {
    grid-template-columns: 1fr 1.4fr;
  }
`;

const Heading = styled.h2`
  margin: 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2.8rem;
  letter-spacing: -2px;

  @media (min-width: 48em) {
    font-size: 5.6rem;
  }
`;

const HeadDesc = styled.p`
  margin: 1.2rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.6rem;
  line-height: 1.6;

  @media (min-width: 48em) {
    font-size: 1.8rem;
    max-width: 54.8rem;
    margin-left: auto;
  }
`;

const Carousel = styled.div`
  max-width: 112rem;
  margin: 3rem auto 0;
  padding: 0 1.6rem 4rem;

  @media (min-width: 48em) {
    margin-top: 8rem;
    padding-bottom: 14rem;
  }
`;

const Viewport = styled.div`
  overflow: hidden;
`;

const Track = styled.div`
  display: flex;
  gap: 1.6rem;
  transition: transform 0.5s ease;
`;

const Card = styled.div`
  position: relative;
  flex: 0 0 25rem;
  height: 35rem;
  border-radius: 2rem;
  box-shadow: 1px -1px 0px 0px #b1c1e7 inset;

  @media (min-width: 48em) {
    flex-basis: 30rem;
    height: 40rem;
  }
`;

const Portrait = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 2rem;
  object-fit: cover;
`;

const Caption = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #ffffff;
  border-radius: 8px 8px 20px 20px;
  box-shadow: 0px -1px 0px 0px #b1c1e7 inset;
  padding: 1.6rem;

  @media (min-width: 48em) {
    padding: 2.4rem;
  }
`;

const Name = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 1.6rem;
  line-height: 2.4rem;

  @media (min-width: 48em) {
    font-size: 2rem;
    line-height: 2.8rem;
  }
`;

const Role = styled.p`
  margin: 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.2rem;

  @media (min-width: 48em) {
    font-size: 1.4rem;
  }
`;

const LinkedIn = styled.a`
  margin-left: auto;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.6rem;
  height: 3.6rem;
  border-radius: 50%;
  background: #063091;
  color: #ffffff;

  @media (min-width: 48em) {
    width: 4.4rem;
    height: 4.4rem;
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 1.6rem;
  margin-top: 2.4rem;
`;

const Arrow = styled.button`
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 8rem;
  height: 5.6rem;
  border: none;
  border-radius: 1.2rem;
  background: #063091;
  color: #ffffff;
  font-size: 2.4rem;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.22.79 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
  </svg>
);

function AboutTeam({ eyebrow, heading, description, members }: AboutTeamProps) {
  const [active, setActive] = useState(0);
  const last = members.length - 1;
  const go = (i: number) => setActive(Math.max(0, Math.min(last, i)));

  return (
    <Band>
      <Head>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <HeadRow>
          <Heading>{heading}</Heading>
          {description ? <HeadDesc>{description}</HeadDesc> : null}
        </HeadRow>
      </Head>

      <Carousel>
        <Viewport>
          <Track style={{ transform: `translateX(calc(-${active} * (30rem + 1.6rem)))` }}>
            {members.map((m, i) => (
              <Card key={`${m.name}-${i}`}>
                {m.imageUrl ? <Portrait src={m.imageUrl} alt={m.name} /> : null}
                <Caption>
                  <span>
                    <Name>{m.name}</Name>
                    {m.designation ? <Role>{m.designation}</Role> : null}
                  </span>
                  {m.linkedin ? (
                    <LinkedIn href={m.linkedin} target="_blank" rel="noreferrer" aria-label={`${m.name} on LinkedIn`}>
                      <LinkedInIcon />
                    </LinkedIn>
                  ) : null}
                </Caption>
              </Card>
            ))}
          </Track>
        </Viewport>

        {members.length > 1 ? (
          <Controls>
            <Arrow onClick={() => go(active - 1)} disabled={active === 0} aria-label="Previous">
              &#8592;
            </Arrow>
            <Arrow onClick={() => go(active + 1)} disabled={active === last} aria-label="Next">
              &#8594;
            </Arrow>
          </Controls>
        ) : null}
      </Carousel>
    </Band>
  );
}

export const aboutTeamBlock: BlockDefinition<AboutTeamProps> = {
  type: 'aboutTeam',
  schema: aboutTeamSchema,
  Renderer: AboutTeam,
};
